import { Box } from "@mui/material";
import { ListBulleted } from "@procore/core-icons";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
    ToolbarButtonEntry,
    toolbarsActions,
    useDispatchToolbars,
    useToolbar,
    useToolbarButtons,
} from "contexts/toolbars";
import { CORE_WINDOW_IDS } from "contexts/windows";
import { useIsPanelOpenUnified, useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";
import ObjectTreeIcon from "media/icons/object-tree.svg?react";

import { PROCORE_BOTTOM_ENTRY } from "../constants";
import { BottomToolbarButton } from "./BottomToolbarButton";

interface GeneralButtonRendererProps {
    toolbarId: string;
}

/**
 * Custom button renderer for general content buttons
 */
function GeneralButtonRenderer({ toolbarId }: GeneralButtonRendererProps) {
    const { t } = useTranslation();
    const buttons = useToolbarButtons(toolbarId);

    const isObjectsOpen = useIsPanelOpenUnified(CORE_WINDOW_IDS.OBJECT_TREE);
    const isPropertiesOpen = useIsPanelOpenUnified(CORE_WINDOW_IDS.PROPERTIES);

    if (buttons.length === 0) {
        return null;
    }

    // Map panel IDs to open states
    const openStates: Record<string, boolean> = {
        [CORE_WINDOW_IDS.OBJECT_TREE]: isObjectsOpen,
        [CORE_WINDOW_IDS.PROPERTIES]: isPropertiesOpen,
    };

    return (
        <Box display="flex" gap={`${PROCORE_BOTTOM_ENTRY.GAP}px`}>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (config.separator || !state.visible) {
                    return null;
                }

                const isActive = openStates[config.id] ?? state.active;
                const baseTooltip = config.tooltip?.toolName || config.title || "";
                const tooltip = !navigator.onLine && state.disabled ? t("notAvailableOffline") : baseTooltip;
                return (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={tooltip}
                        active={isActive}
                        disabled={state.disabled}
                        onClick={config.onClick}
                    />
                );
            })}
        </Box>
    );
}

/**
 * Content for the General tab - opens side panels or drawers based on screen aspect ratio
 */
export function BottomToolbarGeneralContent() {
    const toolbarDispatch = useDispatchToolbars();
    const { togglePanel } = useUnifiedPanelToggle();

    // Keep a ref so onClick closures always call the latest togglePanel without
    // needing to recreate the buttons array on every panelMode flip. This prevents
    // addToolbar from becoming a no-op on re-runs (it's add-only) which would leave
    // stale onClick handlers after sidePanel <-> drawer transitions.
    const togglePanelRef = useRef(togglePanel);
    togglePanelRef.current = togglePanel;

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            {
                config: {
                    id: CORE_WINDOW_IDS.OBJECT_TREE,
                    icon: <ObjectTreeIcon />,
                    onClick: () => togglePanelRef.current(CORE_WINDOW_IDS.OBJECT_TREE),
                    title: "Objects",
                    tooltip: {
                        toolName: "Object Tree",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: CORE_WINDOW_IDS.PROPERTIES,
                    icon: <ListBulleted />,
                    onClick: () => togglePanelRef.current(CORE_WINDOW_IDS.PROPERTIES),
                    title: "Properties",
                    tooltip: {
                        toolName: "Object Properties",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const toolbarId = "bottom-toolbar-general";
    const toolbarExists = useToolbar(toolbarId) !== undefined;

    // Ref guard: updated synchronously inside the effect so it's already true
    // when React Strict Mode re-runs effects in the same commit (before the
    // context state update from the first dispatch has re-rendered the component).
    // toolbarExists handles the case where the component remounts with a fresh ref
    // but the ToolbarProvider state (and SDK-added buttons) has persisted.
    const toolbarDispatchedRef = useRef(false);

    useEffect(() => {
        if (toolbarDispatchedRef.current || toolbarExists) {
            return;
        }
        toolbarDispatchedRef.current = true;
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );
        // Intentionally no cleanup: preserves SDK-added buttons across re-renders
    }, [toolbarDispatch, buttons, toolbarExists]);

    return <GeneralButtonRenderer toolbarId={toolbarId} />;
}
